import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type RingMode = "priority" | "callActivity" | "stageStatus" | "off";

export interface RingColors {
  missedCall: string;
  answeredCall: string;
  noAnswer: string;
  incomingUnanswered: string;
}

export interface StageStatusColors {
  qualifying: string;
  rejection: string;
}

export interface PriorityColors {
  high: string;
  medium: string;
  cold: string;
}

// Single source of truth for the hex priority colors. Exported so non-React
// utilities (e.g. `getPriorityColors` fallback) can use the same values without
// going through the CSS variables — those get re-mapped by `@theme inline` in
// `styles/index.css` (e.g. `--color-medium` is overridden to the very light
// `--color-track`), which would make the avatar rings nearly invisible.
export const DEFAULT_PRIORITY_COLORS: PriorityColors = {
  high: "#e82c4e",
  medium: "#ff953b",
  cold: "#3bd8ff",
};

/** `UserViewPreference.viewKey` for the drawer tab order: `{ order: string[] }`. */
export const DRAWER_TABS_VIEW_KEY = "leadDrawerTabs";

export interface ManagePreferences {
  ringMode: RingMode;
  callActivityColors: RingColors;
  stageStatusColors: StageStatusColors;
  priorityColors: PriorityColors;
  drawerTabOrder: string[];
}

const initialState: ManagePreferences = {
  ringMode: "priority",

  callActivityColors: {
    missedCall: "#e82c4e",
    answeredCall: "#218760",
    noAnswer: "#ff953b",
    incomingUnanswered: "#8C52FF",
  },

  stageStatusColors: {
    qualifying: "#218760",
    rejection: "#e82c4e",
  },

  priorityColors: { ...DEFAULT_PRIORITY_COLORS },

  drawerTabOrder: [],
};

const managePreferencesSlice = createSlice({
  name: "managePreferences",
  initialState,
  reducers: {
    setRingMode(state, action: PayloadAction<RingMode>) {
      state.ringMode = action.payload;
    },
    setCallActivityColor(
      state,
      action: PayloadAction<{ key: keyof RingColors; color: string }>
    ) {
      state.callActivityColors[action.payload.key] = action.payload.color;
    },
    setStageStatusColor(
      state,
      action: PayloadAction<{ key: keyof StageStatusColors; color: string }>
    ) {
      state.stageStatusColors[action.payload.key] = action.payload.color;
    },
    setPriorityColor(
      state,
      action: PayloadAction<{ key: keyof PriorityColors; color: string }>
    ) {
      state.priorityColors[action.payload.key] = action.payload.color;
    },
    setDrawerTabOrder(state, action: PayloadAction<string[]>) {
      state.drawerTabOrder = action.payload;
    },
    /** Boot hydrate from the DB row (`leadDrawerTabs`). A distinct action so
     *  the write-through middleware never echoes a hydrate back as a save. */
    hydrateDrawerTabOrder(state, action: PayloadAction<string[]>) {
      state.drawerTabOrder = action.payload;
    },
    resetManagePreferences() {
      return initialState;
    },
  },
});

export const {
  hydrateDrawerTabOrder,
  setRingMode,
  setCallActivityColor,
  setStageStatusColor,
  setPriorityColor,
  setDrawerTabOrder,
  resetManagePreferences,
} = managePreferencesSlice.actions;

export default managePreferencesSlice.reducer;
